import { getCookie, getSearchParam } from '@/util/url'
import { IGif } from '@giphy/js-types'
import getAdTestResponse from './ad-mocks/ad-test-response'
import { processGifImages, processGifVideoAssets } from './ads-util'
import { giphyLocalUrl, publicRuntimeConfig } from './env'

export type AdEndpointViews =
    // spotlights that appear in a grid
    | 'xl_carousel_banner'
    | 'home_video_spotlight'
    | 'search_results_video_spotlight'
    | 'gif_details_video_spotlight'
    | 'homepage_banner' // bouncer banner
    // geo views
    | 'home' // geo home page banner
    | 'gif_details' // gif detail banner
    | 'search_results' // search page banner
    | 'home_grid_0' // geo home grid
    | 'gif_details_grid_0' // geo gif details grid
    | 'search_results_grid_0' // geo search grid

export const gridPathnameMap: Record<string, AdEndpointViews> = {
    '/gifs': 'gif_details_grid_0',
    '/search': 'search_results_grid_0',
    '/': 'home_grid_0',
}

export type AdDecisionAnalytics = {
    start?: string
    firstq?: string
    midpoint?: string
    thirdq?: string
    complete?: string
    mute?: string
    unmute?: string
    pause?: string
    rewind?: string
    resume?: string
    // Allow unexpected/extra event names without losing type safety for known ones
    [eventName: string]: string | undefined
}

export type AdDecision = {
    gif: IGif
    subdecisions?: AdDecision[]
    cta_text?: string
    cta_url?: string
    impression_url?: string
    click_url?: string
    analytics?: AdDecisionAnalytics
}

export type AdResponse = {
    decisions: Record<string, AdDecision | null>
}

export const hasAnyAd = (adResponse?: AdResponse) => {
    const decisions = adResponse?.decisions || {}
    return Object.keys(decisions).some((key) => {
        return decisions[key] !== null
    })
}
export const getAdDecisionByType = (response: AdResponse | undefined, id?: AdEndpointViews) =>
    id
        ? (response?.decisions?.[
            id === 'search_results_video_spotlight' ? 'search_results_video_spotlight' : id
        ] as AdDecision)
        : undefined

export const hasAd = (type: AdEndpointViews, adResponse?: AdResponse) => {
    return !!getAdDecisionByType(adResponse, type)
}

export const mergeResponses = (response1?: AdResponse | undefined, response2?: AdResponse): AdResponse => {
    return {
        decisions: { ...(response1?.decisions || {}), ...(response2?.decisions || {}) },
    }
}

export type AdEndpointRequestOptions = {
    searchParams?: URLSearchParams
    views: AdEndpointViews[]
    rating?: string
    keywords?: string[]
    cookie?: string
    accessToken?: string
    headers?: HeadersInit
}

export type AdEndpointRequestBody = {
    keywords: string[]
    rating: string
    views: AdEndpointViews[]
    platform: string
    testing: boolean
    use_mock: boolean
}

/**
 * Fetch from the ads endpoint, this needs to be invoked from both the server and the client
 */
export const fetchAdFromEndpoint = async ({
    searchParams,
    views,
    rating = 'unrated',
    keywords = [],
    accessToken = '',
    cookie = '',
    headers: incomingHeaders,
}: AdEndpointRequestOptions): Promise<[AdResponse?, string?]> => {
    const body: AdEndpointRequestBody = {
        keywords,
        rating,
        views,
        platform: 'web',
        testing: getCookie('ads_dev_network', cookie) === 'true',
        use_mock: getCookie('ads_use_mock', cookie) === 'true',
    }
    // load test1.json here if there is a searchParam that equals ad_test=1
    const adTestValue = parseInt(getSearchParam('ad_test', searchParams) || '')
    if (!isNaN(adTestValue)) {
        return await getAdTestResponse(adTestValue)
    }
    if (publicRuntimeConfig.isPlaywright) {
        return [, 'Running Playwright test']
    }
    const fetchUrl = `${giphyLocalUrl}/api/v2/ads/`
    try {
        let headers: HeadersInit = {
            'content-type': 'application/json',
            ...(accessToken ? { authorization: `Token ${accessToken}` } : {}),
            cookie,
            'X-CSRFToken': getCookie('csrftoken', cookie),
        }
        if (incomingHeaders) {
            headers = { ...headers, ...incomingHeaders }
        }
        const response = await fetch(fetchUrl, {
            method: 'POST',
            body: JSON.stringify(body),
            headers,
        })
        // console.log(`ad endpoint request ${fetchUrl}`, JSON.stringify(body))
        if (response.ok) {
            try {
                const result = (await response.json()) as AdResponse

                // For each decision, if click_url and gif exist, set gif.bottle_data?.tdata?.click_out_url = click_url
                // Also append `ap=1` to every URL under gif.images for ad attribution, except subdecisions

                const processDecision = (decision?: AdDecision, appendAp: boolean = true, adProductType?: string) => {
                    if (!decision) return

                    if (decision.gif) {
                        if (decision.click_url) {
                            const gif = decision.gif
                            gif.bottle_data = gif.bottle_data ?? {}
                            gif.bottle_data.tdata = gif.bottle_data.tdata ?? {}
                            gif.bottle_data.tdata.click_out_url = decision.click_url
                        }

                        processGifImages(decision.gif, appendAp)

                        if (decision.gif.type === 'video') {
                            processGifVideoAssets(decision.gif, appendAp)
                        }
                    }

                    // for subdecisions on xl_carousel_banner, don't append ap=1 to avoid showing "Promoted" pill
                    const shouldAppendApForSubdecisions = adProductType !== 'xl_carousel_banner'
                    ; (decision.subdecisions || []).forEach(sub =>
                        processDecision(sub, shouldAppendApForSubdecisions, adProductType)
                    )
                }
                Object.entries(result?.decisions || {}).forEach(([adType, decision]) =>
                    processDecision(decision || undefined, true, adType)
                )

                return [result]
            } catch (jsonError) {
                console.error('Failed to parse ad endpoint response as JSON', jsonError)
                return [, `Status:${response.status} ${jsonError}`]
            }
        }
        let text = ''
        try {
            text = await response.text()
        } catch (_e) { }
        return [, `Status:${response.status} ${text}`]
    } catch (e) {
        console.error(e)
        return [, e as any]
    }
}
